import { Dialog } from '@angular/cdk/dialog';
import { CommonModule, Location } from '@angular/common';
import { HttpBackend, HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_BOOTSTRAP_LISTENER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalService } from '@azure/msal-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  QIMA_ASSETS_PATH_TOKEN,
  QIMA_DATE_FACTORY_TOKEN,
  QIMA_TRANSLATION_FACTORY_TOKEN,
  QimaDateModule,
  QimaModule,
  QimaSnackbarService,
  QimaTranslationModule,
} from '@qima/ngx-qima';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { routes } from './app.routes';
import { dateFactory } from './core/factories/date.factory';
import { logrocketBootstrapFactory } from './core/factories/logrocket-bootstrap.factory';
import { httpAuthInterceptor } from './core/interceptors/http-auth.interceptor';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { msalConfigFactory, msalGuardConfigFactory, msalInterceptorConfigFactory } from './core/services/authentication/office365.service';
import { LogrocketInitService } from './core/services/logrocket-init/logrocket-init.service';
import { QimaTranslationFactoryService } from './core/services/qima-translation-factory.service';
import { workflowMockInterceptor } from './interceptors/workflow-mock.interceptor';

// Factory function for the translate loader - use HttpBackend to avoid circular dependency
export function HttpLoaderFactory(httpBackend: HttpBackend) {
  return new TranslateHttpLoader(new HttpClient(httpBackend), './assets/i18n/', `.json?v=${new Date().getTime()}`);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([workflowMockInterceptor, httpAuthInterceptor, httpErrorInterceptor])),
    importProvidersFrom(
      CommonModule,
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpBackend],
        },
      }),
      QimaModule.forRoot(),
      QimaDateModule,
      QimaTranslationModule.forRoot(),
      AngularSvgIconModule.forRoot()
    ),
    { provide: MSAL_INSTANCE, useFactory: msalConfigFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    {
      provide: MsalService,
      useFactory: (instance: unknown, location: Location) =>
        new MsalService(instance as import('@azure/msal-browser').IPublicClientApplication, location),
      deps: [MSAL_INSTANCE, Location],
    },
    {
      provide: QIMA_ASSETS_PATH_TOKEN,
      useValue: 'ngx-qima/assets/',
    },
    {
      provide: QIMA_DATE_FACTORY_TOKEN,
      useFactory: dateFactory,
    },
    {
      provide: QIMA_TRANSLATION_FACTORY_TOKEN,
      useClass: QimaTranslationFactoryService,
    },
    QimaSnackbarService,
    Dialog,
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      deps: [LogrocketInitService],
      useFactory: logrocketBootstrapFactory,
    },
  ],
};
